/* eslint-disable no-plusplus */
import { createRetryWrapper, IRetryConfig } from '@intelligence/v3/utils/enhancer';

/**
 * 分页配置接口
 * @author lv.chen
 */
export interface IPaginationConfig<TResponse = any, TParams = IRecord> {
  /** 页码字段名，默认为 'page' */
  pageField: string;
  /** 每页大小字段名，默认为 'page_size' */
  pageSizeField: string;
  /** 数据列表字段名，默认为 'list' */
  listField: string;
  /** 总数字段名，默认为 'total' */
  totalField: string;
  /** 每次获取到分页数据后的回调，回传接口返回的完整数据 */
  onPageFetched?: (response: TResponse, params: TParams, ...restParams: any[]) => void;
}

/**
 * 分页服务函数类型
 * @author lv.chen
 */
export type IPaginationService<TParams = IRecord, TResponse = any> = (
  params: TParams,
  ...restParams: any[]
) => Promise<TResponse>;

/**
 * 分页响应数据接口
 * @author lv.chen
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export interface IPaginationResponse<TData = any> {
  /** 其他字段 */
  [key: string]: any;
}

/**
 * 自动分页获取所有数据的高阶函数
 *
 * 该函数用于处理需要分页获取大量数据的场景，自动循环调用分页接口直到获取完所有数据
 *
 * @author lv.chen
 * @param service - 分页查询服务函数
 * @param config - 分页配置，包含字段名映射
 * @param retryConfig - 重试配置
 * @returns 返回一个异步函数，该函数接收查询参数并返回所有数据的数组
 *
 * @example
 * ```typescript
 * const getAllUsers = fetchDownloadDataAutoPagination(
 *   getUserList,
 *   {
 *     pageField: 'page',
 *     pageSizeField: 'page_size',
 *     listField: 'users',
 *     totalField: 'total_count'
 *   }
 * );
 *
 * const allUsers = await getAllUsers({ status: 'active', page_size: 100 });
 * ```
 */
export const fetchDownloadDataAutoPagination = <
  TParams extends IRecord = IRecord,
  TData = any,
  TResponse extends IPaginationResponse<TData> = IPaginationResponse<TData>,
>(
  service: IPaginationService<TParams, TResponse>,
  config: IPaginationConfig<TResponse, TParams> = {
    pageField: 'page',
    pageSizeField: 'page_size',
    listField: 'list',
    totalField: 'total',
  },
  retryConfig: IRetryConfig = {
    maxRetries: 3,
    retryDelay: 1000,
  },
) => {
  // 参数验证
  if (typeof service !== 'function') {
    throw new Error('fetchDownloadDataAutoPagination: service must be a function.');
  }

  if (!config || typeof config !== 'object') {
    throw new Error('fetchDownloadDataAutoPagination: config must be an object.');
  }

  const {
    pageField = 'page',
    pageSizeField = 'page_size',
    listField = 'list',
    totalField = 'total',
    onPageFetched,
  } = config;

  /**
   * 验证响应数据格式
   * @param response - 响应数据
   * @param currentPage - 当前页码（用于错误信息）
   * @returns 验证通过的响应数据
   */
  const validateResponse = (response: TResponse, currentPage: number): TResponse => {
    if (!response || typeof response !== 'object') {
      throw new Error(`Invalid response format for page ${currentPage}: response must be an object`);
    }

    if (!Array.isArray(response[listField])) {
      throw new Error(`Invalid response format for page ${currentPage}: ${listField} must be an array`);
    }

    if (typeof response[totalField] !== 'number' || response[totalField] < 0) {
      throw new Error(`Invalid response format for page ${currentPage}: ${totalField} must be a non-negative number`);
    }

    return response;
  };

  /**
   * 调用分页服务并验证响应
   * @param currentPage - 当前页码
   * @param params - 请求参数
   * @param restParams - 其他参数
   * @returns 验证后的响应数据
   */
  const callServiceAndValidate = async (
    currentPage: number,
    params: TParams,
    ...restParams: any[]
  ): Promise<TResponse> => {
    const response = await service({ ...params, [pageField]: currentPage }, ...restParams);
    return validateResponse(response, currentPage);
  };

  // 创建带重试功能的服务调用函数
  const callServiceWithRetry = createRetryWrapper(
    callServiceAndValidate,
    retryConfig,
    'fetchDownloadDataAutoPagination',
  );

  /**
   * 执行自动分页查询（并发优化版本）
   * @param params - 查询参数
   * @returns 所有数据的数组
   */
  return async (params: TParams, ...restParams: any[]): Promise<TResponse> => {
    // 参数验证
    if (!params || typeof params !== 'object') {
      throw new Error('fetchDownloadDataAutoPagination: params must be an object.');
    }

    const pageSize = Number(params[pageSizeField]) || 5000; // 调整默认值为500，更合理
    let totalCount = 0;
    let totalPages = 1;

    try {
      // 第一步：获取第一页数据，确定总页数
      const firstPageResponse = await callServiceWithRetry(1, params, ...restParams);
      const firstPageData = firstPageResponse[listField] || [];

      // 调用回调通知第一页数据已获取
      onPageFetched?.(firstPageResponse, params, ...restParams);

      totalCount = Number(firstPageResponse[totalField]) || 0;

      // 如果总数为0，直接返回空数组
      if (totalCount === 0) {
        return { ...firstPageResponse, [listField]: [] };
      }

      // 计算总页数
      totalPages = Math.ceil(totalCount / pageSize);

      // 验证总页数的合理性
      if (totalPages <= 0) {
        throw new Error(
          `Invalid pagination calculation: totalPages=${totalPages}, totalCount=${totalCount}, pageSize=${pageSize}`,
        );
      }

      // 如果只有一页数据，直接返回
      if (totalPages === 1) {
        return { ...firstPageResponse, [listField]: firstPageData };
      }

      // 第二步：并发获取剩余页面数据
      const remainingPagePromises: Promise<{ page: number; data: TData[]; success: boolean; error?: string }>[] = [];

      for (let page = 2; page <= totalPages; page++) {
        const pagePromise = callServiceWithRetry(page, params, ...restParams)
          .then((response) => {
            // 调用回调通知当前页数据已获取
            onPageFetched?.(response, params, ...restParams);
            return {
              page,
              data: response[listField] || [],
              success: true,
            };
          })
          .catch((error) => {
            return {
              page,
              data: [] as TData[],
              success: false,
              error: error instanceof Error ? error.message : String(error),
            };
          });

        remainingPagePromises.push(pagePromise);
      }

      // 等待所有并发请求完成
      const remainingPagesResults = await Promise.all(remainingPagePromises);

      // 检查是否有失败的请求
      const failedPages = remainingPagesResults.filter((result) => !result.success);
      if (failedPages.length > 0) {
        const failedPageNumbers = failedPages.map((result) => result.page).join(', ');
        const firstError = failedPages[0].error;
        throw new Error(`Failed to fetch pages: ${failedPageNumbers}. First error: ${firstError}`);
      }

      // 第三步：按页码顺序合并所有数据
      const allData: TData[] = [...firstPageData];

      // 按页码排序并合并数据
      remainingPagesResults
        .sort((a, b) => a.page - b.page)
        .forEach((result) => {
          allData.push(...result.data);
        });

      // 验证最终数据完整性
      if (allData.length > totalCount) {
        console.warn(
          `fetchDownloadDataAutoPagination: Retrieved more data than expected. Expected: ${totalCount}, Actual: ${allData.length}`,
        );
      }

      return { ...firstPageResponse, [listField]: allData };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      throw new Error(`fetchDownloadDataAutoPagination: Failed to fetch all data. ${errorMessage}`);
    }
  };
};

